import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'

export const theme = createTheme({
  palette: {
    primary: {
      main: '#2d6a4f',
      light: '#3a8a64',
      dark: '#235a40',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f5f3ef',
      light: '#faf9f7',
      dark: '#e8e4dd',
      contrastText: '#2c2a26',
    },
    success: {
      main: '#2d6a4f',
      light: '#e8f5ee',
      dark: '#235a40',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#7a5a20',
      light: '#f5f0e0',
      dark: '#5a4010',
      contrastText: '#ffffff',
    },
    error: {
      main: '#8a3020',
      light: '#f5e8e0',
      dark: '#6a2015',
      contrastText: '#ffffff',
    },
    background: {
      default: '#faf9f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c2a26',
      secondary: '#7a766f',
    },
    divider: '#e8e4dd',
  },
  typography: {
    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 600 },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', color: '#7a766f' },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  components: {
    // Use MUI's compact sizing so input padding and label positions stay in sync.
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiPickersTextField: {
      defaultProps: { size: 'small' },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          borderRadius: 4,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: {
          fontSize: '0.8125rem',
          padding: '4px 10px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #e8e4dd',
          borderRadius: 4,
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': { paddingBottom: 16 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 4px rgba(44,42,38,0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 24,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '0.875rem',
          backgroundColor: '#ffffff',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2d6a4f',
            borderWidth: 1,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0ab9f',
          },
        },
        notchedOutline: {
          borderColor: '#e8e4dd',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: '#2d6a4f',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f5f3ef',
          borderRight: '1px solid #e8e4dd',
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#2c2a26',
          boxShadow: 'none',
          borderBottom: '1px solid #e8e4dd',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
          '@media (min-width: 600px)': { minHeight: 56 },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f7f5f2',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: '#7a766f',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid #e8e4dd',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e8e4dd',
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: '1px solid #e8e4dd',
          boxShadow: '0 8px 32px rgba(44,42,38,0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          color: '#2c2a26',
          borderBottom: '1px solid #e8e4dd',
          padding: '16px 20px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #e8e4dd',
          padding: '12px 20px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e8e4dd',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&.Mui-selected': {
            backgroundColor: '#e8f5ee',
            color: '#2d6a4f',
            '&:hover': { backgroundColor: '#d4ede0' },
            '& .MuiListItemIcon-root': { color: '#2d6a4f' },
          },
          '&:hover': { backgroundColor: '#eeebe5' },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: '#7a766f',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 44,
          '&.Mui-selected': { color: '#2d6a4f' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#2d6a4f',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#2d6a4f',
            '& + .MuiSwitch-track': { backgroundColor: '#2d6a4f' },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: '#2d6a4f' },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: '#e8e4dd',
        },
        bar: {
          borderRadius: 999,
          backgroundColor: '#2d6a4f',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2c2a26',
          fontSize: '0.75rem',
          borderRadius: 4,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '0.875rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#2d6a4f',
          '&:hover': { color: '#235a40' },
        },
      },
    },
  },
})
